import React, { useState, useContext } from 'react'
import I18n from 'i18n'
import DatePicker from 'react-datepicker'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Button from '../../atomic/atoms/Button'
import { dateAndTimePickerDefaultOptions } from '../../common/Constants'
import { ModalContext } from '../../modal/ModalProvider'
import Training from '../../common/types/Training'

interface Props {
  selectedTrainings: Training[]
  onConfirm: (dates: Date[]) => void
}

const DuplicateTrainingModal: React.FC<Props> = ({ selectedTrainings, onConfirm }) => {
  const { hide } = useContext(ModalContext)
  const [dates, setDates] = useState<Array<Date | null>>([null])

  const addDateRow = (): void => {
    setDates([...dates, null])
  }

  const removeDateRow = (index: number): void => {
    if (dates.length <= 1) return
    setDates(dates.filter((_, i) => i !== index))
  }

  const updateDate = (index: number, value: Date | null): void => {
    const next = [...dates]
    next[index] = value
    setDates(next)
  }

  const allFilled = dates.every(d => d !== null)

  const handleConfirm = (): void => {
    if (!allFilled) return
    onConfirm(dates as Date[])
    hide()
  }

  const handleCancel = (): void => {
    hide()
  }

  return (
    <>
      <div className='row'>
        <div className='col s12'>
          <div className='text-heavy text-primary-color text-l'>
            {I18n.t('components.trainings.duplicate_modal.title', { count: selectedTrainings.length })}
          </div>
          <p className='text-muted'>
            {I18n.t('components.trainings.duplicate_modal.description')}
          </p>
        </div>
      </div>

      <div className='row'>
        <div className='col s12'>
          {dates.map((date, index) => (
            <div key={index} className='margin-bottom' style={{ display: 'flex', alignItems: 'center' }}>
              <DatePicker
                {...dateAndTimePickerDefaultOptions()}
                id={`duplicate-date-picker-${index}`}
                selected={date}
                onChange={value => updateDate(index, value)}
                placeholderText={I18n.t('components.trainings.duplicate_modal.date_placeholder')}
              />
              {dates.length > 1 && (
                <div
                  className='margin-left pointer-grow text-accent'
                  onClick={() => removeDateRow(index)}
                  aria-label={I18n.t('components.trainings.duplicate_modal.remove_date')}
                  role='button'
                >
                  <FontAwesomeIcon icon='times' />
                </div>
              )}
            </div>
          ))}
          <div className='pointer margin-top' onClick={addDateRow}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className='text-primary-color margin-right'><FontAwesomeIcon icon='plus' /></div>
              <div className='text-muted emphasized'>{I18n.t('components.trainings.duplicate_modal.add_date')}</div>
            </div>
          </div>
        </div>
      </div>

      <div className='row margin-top'>
        <div className='col s12' style={{ display: 'flex', flexDirection: 'row-reverse' }}>
          <Button autowidth onClick={handleConfirm} isDisabled={!allFilled}>
            <FontAwesomeIcon icon='copy' className='button-icon' />
            {I18n.t('components.trainings.duplicate_modal.confirm')}
          </Button>
          <Button autowidth type='secondary' onClick={handleCancel}>
            {I18n.t('components.trainings.duplicate_modal.cancel')}
          </Button>
        </div>
      </div>
    </>
  )
}

export default DuplicateTrainingModal
